import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import {
  deleteRothBeneficiary,
  addRothBeneficiary,
  updateRothBeneficiary,
} from '../../../../redux/modules/rothConversionTransaction/actions';
import Button from '../../../Shared/Button/Button';
import If from '../../../Shared/If/If';
import BeneficiaryInput from '../BeneficiaryInput/BeneficiaryInput';
import BeneficiaryDisplayOnly from '../BeneficiaryInput/DisplayOnly';
import { BENEFICIARY_CATEGORY } from '../../../../settings/settings';

import './BeneficiaryList.scss';
import BeneficaryListMobile from './BeneficiaryListMobile/BeneficaryListMobile';

const BeneficiaryList = ({
  displayOnly,
  disabled = true,
  beneficiaries = {},
}) => {
  const [showNoPrimaryError, setShowNoPrimaryError] = useState(false);
  const dispatch = useDispatch();
  const { primary = [{}], secondary = [], percentageCheck } = beneficiaries;

  const onDelete = useCallback(
    (id, category) => {
      // can delete all secondary but cannot delete primary if theres only 1
      if (
        category !== BENEFICIARY_CATEGORY.PRIMARY ||
        beneficiaries[category].length > 1
      ) {
        dispatch(deleteRothBeneficiary(id, category));
      }
    },
    [dispatch, beneficiaries]
  );

  const updateBeneficiary = useCallback(
    (id, props, category) => {
      dispatch(updateRothBeneficiary({ id, props, category }));
    },
    [dispatch]
  );

  /**
   * On add secondary, will check if there is a valid primary first.
   */
  const onAddSecondary = useCallback(() => {
    const hasValidPrimary = primary.find((b) => b.isValid);
    if (hasValidPrimary) {
      dispatch(addRothBeneficiary(uuidv4(), BENEFICIARY_CATEGORY.SECONDARY));
    }

    setShowNoPrimaryError(!hasValidPrimary);
  }, [dispatch, primary]);

  return (
    <div>
      <div>
        <BeneficaryListMobile
          displayOnly={displayOnly}
          disabled={disabled}
          beneficiaries={beneficiaries}
        />
      </div>
      <div className="beneficiaryList">
        <div className="blocked_data beneficiaryList_row">
          <h2 className="beneficiaryList-header">Primary Beneficiary Name</h2>
          {primary.map((beneficiary, i) =>
            displayOnly ? (
              <BeneficiaryDisplayOnly
                key={beneficiary.taxId + i}
                beneficiary={beneficiary}
              />
            ) : (
              <BeneficiaryInput
                key={beneficiary.id + i}
                disabled={disabled}
                beneficiary={beneficiary}
                onDelete={(id) => onDelete(id, BENEFICIARY_CATEGORY.PRIMARY)}
                onUpdate={({ id, props }) =>
                  updateBeneficiary(id, props, BENEFICIARY_CATEGORY.PRIMARY)
                }
              />
            )
          )}
          <If test={!displayOnly && !disabled && !percentageCheck?.primary}>
            <div className="beneficiaryList_error --percentage">
              Primary Beneficiaries should total to <strong>100%</strong>
            </div>
          </If>
        </div>
        <If test={!displayOnly && !disabled}>
          <div className="beneficiaryList_footer">
            <Button
              className="beneficiaryList_add-button"
              buttonType="secondary-slim"
              onClick={() =>
                dispatch(
                  addRothBeneficiary(uuidv4(), BENEFICIARY_CATEGORY.PRIMARY)
                )
              }
            >
              Add Primary Beneficiary
            </Button>
          </div>
        </If>
        <If test={secondary.length}>
          <div className="blocked_data beneficiaryList_row">
            <h2 className="beneficiaryList-header">
              Secondary Beneficiary Name
            </h2>
            {secondary.map((beneficiary, i) =>
              displayOnly ? (
                <BeneficiaryDisplayOnly
                  key={beneficiary.taxId + i}
                  beneficiary={beneficiary}
                />
              ) : (
                <BeneficiaryInput
                  key={beneficiary.id + i}
                  disabled={disabled}
                  beneficiary={beneficiary}
                  onDelete={(id) =>
                    onDelete(id, BENEFICIARY_CATEGORY.SECONDARY)
                  }
                  onUpdate={({ id, props }) =>
                    updateBeneficiary(id, props, BENEFICIARY_CATEGORY.SECONDARY)
                  }
                />
              )
            )}
            <If test={!displayOnly && !disabled && !percentageCheck?.secondary}>
              <div className="beneficiaryList_error --percentage">
                Secondary Beneficiaries should total to <strong>100%</strong>
              </div>
            </If>
          </div>
        </If>
        <If test={!displayOnly && !disabled}>
          <div className="beneficiaryList_footer">
            <Button
              className="beneficiaryList_add-button"
              buttonType="secondary-slim"
              onClick={onAddSecondary}
            >
              Add Secondary Beneficiary
            </Button>
          </div>
        </If>
        <If test={showNoPrimaryError}>
          <div className="beneficiaryList_error">
            A valid <strong>Primary Beneficiary</strong> is required to have a{' '}
            <strong>Secondary Beneficiary</strong> on file.
          </div>
        </If>
      </div>
    </div>
  );
};

export default BeneficiaryList;
